import { useMemo } from 'react';
import { useInfiniteOwnedUserImages } from '@/hooks/content-generator/useInfiniteOwnedUserImages';
import { useInfiniteUserPages } from '@/hooks/content-generator/useInfiniteUserPages';
import { useInfiniteUserResearch } from '@/hooks/content-generator/useInfiniteUserResearch';

interface UseContentImportProps {
  contentType: 'images' | 'research' | 'pages';
  projectId: string; // Used to exclude research already in this project
  searchQuery: string;
  limit?: number;
}

/**
 * Hook for importing content (images, research, pages) across all accessible projects
 * Excludes content already assigned to the current project
 */
export const useContentImport = ({
  contentType,
  projectId,
  searchQuery,
  limit = 50
}: UseContentImportProps) => {
  // Create a dummy query result for unused content types to avoid unnecessary API calls
  const emptyQueryResult = {
    data: undefined,
    isLoading: false,
    isFetchingNextPage: false,
    hasNextPage: false,
    fetchNextPage: () => Promise.resolve({ pages: [], pageParams: [] } as any),
    error: null
  };

  // Only execute the query for the requested content type
  const imageQuery = contentType === 'images' 
    ? useInfiniteOwnedUserImages({
        searchQuery,
        limit,
        excludeProjectId: projectId
      })
    : emptyQueryResult;

  const pageQuery = contentType === 'pages'
    ? useInfiniteUserPages({
        searchQuery,
        limit,
        excludeProjectId: projectId
      })
    : emptyQueryResult;

  const researchQuery = contentType === 'research'
    ? useInfiniteUserResearch({
        searchQuery,
        limit,
        excludeProjectId: projectId
      })
    : emptyQueryResult;

  // Use the appropriate query based on content type
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error
  } = contentType === 'pages' ? pageQuery : 
      contentType === 'research' ? researchQuery : 
      imageQuery;

  // Flatten all pages into a single array
  const allItems = useMemo(() => {
    return data?.pages.flatMap(page => page.data) || [];
  }, [data]);

  // Total count from the first page - handle different data structures
  const totalCount = useMemo(() => {
    if (!data?.pages || data.pages.length === 0) return 0;
    const firstPage = data.pages[0];
    return (firstPage as any)?.totalCount || (firstPage as any)?.total || allItems.length;
  }, [data, allItems.length]);

  return {
    data: allItems,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    totalCount,
    error
  };
}; 